import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { formatCurrency } from '@/common/utils/formatCurrency';
import { getMileageUnit } from '@/common/utils/getMileageUnit';
import i18n from '@/config/i18n';
import { useSelectedDebitNote } from '@/modules/invoicing/hooks/useSelectedDebitNote';
import { IDebitNotePaginated } from '@/modules/invoicing/services/getPaginateDebitNote';

import * as styles from './styles';

type TableRowProps = {
  data: IDebitNotePaginated['DebitNoteSignChassisInstallments'][0];
};

export function TableRow({ data }: TableRowProps) {
  const { t } = useTranslation('invoicing/openInvoices');
  const mileageUnit = getMileageUnit(
    data?.SignChassisInstallments?.SignChassis?.DealProduct?.operationType,
  );
  const { selectedDebitNote } = useSelectedDebitNote.getState();

  const adjustmentDetails =
    data?.SignChassisInstallments?.metadata?.adjustmentDetails[
      data?.SignChassisInstallments?.metadata?.adjustmentDetails.length - 1
    ];

  return (
    <tr className={styles.container()}>
      <td className={styles.tableData()}>
        {data.SignChassisInstallments.SignChassis.chassis}

        <span className={styles.tableSubData()}>
          {data?.SignChassisInstallments.SignChassis.codeContract} (
          {t('contract')})
        </span>

        <span className={styles.tableSubData()}>
          {selectedDebitNote?.Sign.Deal.Plan.title}
        </span>
      </td>

      <td className={styles.tableData()}>
        {data?.SignChassisInstallments?.numberInstallment}/
        {data?.SignChassisInstallments?.SignChassis?.quantityInstallments}
        <span className={styles.tableSubData()}>
          {format(
            new Date(data?.SignChassisInstallments?.SignChassis?.startDate),
            'dd/MM/yyyy',
          )}{' '}
          ({t('start')})
        </span>
        <span className={styles.tableSubData()}>
          {format(
            new Date(data?.SignChassisInstallments?.SignChassis?.endDate),
            'dd/MM/yyyy',
          )}{' '}
          ({t('end')})
        </span>
      </td>

      <td className={styles.tableData()}>
        {Intl.NumberFormat(i18n.language, {
          style: 'decimal',
          maximumFractionDigits: 0,
        }).format(
          data?.SignChassisInstallments?.SignChassis?.finalMileage,
        )}{' '}
        {mileageUnit} ({t('total')})
        <span className={styles.tableSubData()}>
          {Intl.NumberFormat(i18n.language, {
            style: 'decimal',
            maximumFractionDigits: 0,
          }).format(data?.SignChassisInstallments?.monthlyMileage)}{' '}
          {mileageUnit} ({t('monthly')})
        </span>
      </td>

      <td className={styles.tableData()}>
        {formatCurrency(
          data?.SignChassisInstallments?.monthlyInstallmentPrice,
          { maximumFractionDigits: 20 },
        )}
        <span className={styles.tableSubData()}>
          {formatCurrency(data?.SignChassisInstallments?.pricePerKilometer, {
            maximumFractionDigits: 20,
          })}
          ({t('valueByKm')})
        </span>

        {adjustmentDetails.accrualBasis && (
          <span className={styles.tableSubData()}>
            {t('ipcaAt')}{' '}
            {format(new Date(adjustmentDetails.applicationDate), 'MM/yyyy')}
          </span>
        )}
      </td>
    </tr>
  );
}
